import _ from "lodash";
import {
  Layouts as GridLayouts,
  Layout as GridLayout
} from "react-grid-layout";

import {
  IWidget,
  UserLayoutPreferences,
  WidgetDisplayTypes
} from "../api/dashboard.api";
import { dashboardLayoutConfig } from "../contexts/DashboardContext";
import { WidgetSlug } from "../api/widget-data.api";

interface MergeLayoutOptionsParams {
  // The layout object within the dashboard detailing user preferences
  dashboardLayout: UserLayoutPreferences;
  // Dashboard widget objects with fixed layout options
  dashboardWidgets: IWidget[];
  // Full width of the container that wraps the grid layout
  containerWidth: number;
}

interface CalculateWidthParams extends Pick<MergeLayoutOptionsParams, "containerWidth"> {
  // The col size we're dealing with, ie lg | md | sm
  sizeSetting: string;
  // The set width in pixels associated to the widget
  width?: number;
}

/**
 * This helper takes the dashboard layout object and list of dashboard widgets and merges configurations
 * which is required to generate the layout object for react-grid-layout.
 */
const mergeLayoutOptions = ({
  dashboardLayout, dashboardWidgets, ...rest
}: MergeLayoutOptionsParams): GridLayouts => {
  // Get the maximum y value from existing items, used for adding new widgets to the bottom
  const maxY = dashboardLayout.lg.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  // Create grid layouts object structure
  const layoutsToRender: GridLayouts = {
    lg: [],
    md: [],
    sm: []
  };

  // Loop arrays of each layout option (lg, md, sm) and merge in set layout options from widgets
  for (const screenSize in dashboardLayout) {
    // Loop through widgets to check for layout options
    dashboardWidgets.forEach((widget: IWidget) => {
      if (widget.widgetType) {
        // retrieve widget type layout options to set up fixed layout options object
        const widgetTypeLayoutOptions = widget.widgetType.layoutOptions;

        // Ensure to calc height and width based on row and column sizes as values set in pixels
        const fixedLayoutOptions = {
          minH: calculateHeight(dashboardLayoutConfig.rowHeight, widgetTypeLayoutOptions?.minH),
          maxH: calculateHeight(dashboardLayoutConfig.rowHeight, widgetTypeLayoutOptions?.maxH),
          maxW: calculateWidth({
            width: widgetTypeLayoutOptions?.maxW,
            sizeSetting: screenSize,
            ...rest
          }),
          minW: calculateWidth({
            width: widgetTypeLayoutOptions?.minW,
            sizeSetting: screenSize,
            ...rest
          })
        };

        // Initialise variable object to for all widget layout options to merge into
        let widgetLayoutOptions: GridLayout;
        // Check for exisiting userLayoutOptions relating to the current widget
        const userLayoutOptions = _.find(dashboardLayout[ screenSize ], dl => parseInt(dl.i) === widget.id);

        // If userLayoutOptions exist merge them with fixed layout options
        // If not set default userLayoutOptions
        if (userLayoutOptions) {
          widgetLayoutOptions = {
            ...fixedLayoutOptions,
            ...userLayoutOptions
          };
        } else {
          const displayType = widget.options.view;

          widgetLayoutOptions = {
            ...fixedLayoutOptions,
            x: 0,
            y: maxY + 1,
            h: getDefaultHeight(widget.widgetType?.slug, displayType, screenSize),
            i: widget.id.toString(),
            w: getDefaultWidth(widget.widgetType?.slug, displayType, screenSize)
          };
        }

        layoutsToRender[ screenSize ].push(widgetLayoutOptions);
      }
    });
  }

  return layoutsToRender;
};

/**
 * Helper used locally to calculate minH and maxH
 *
 * @param rowHeight set row height for grid layout
 * @param height is minH or maxH in pixels, we aim to return height value for grid layout.
 */
const calculateHeight = (rowHeight: number, height?: number): number | undefined => {
  // Account for posibility height not set in layoutOptions
  if (!height) {
    return;
  }

  return Math.round(height / rowHeight);
};

/**
 * Helper used locally to calculate minW and maxW
 */
const calculateWidth = ({
  containerWidth, sizeSetting, width
}: CalculateWidthParams): number | undefined => {
  // Account for posibility width not set in layoutOptions
  if (!width) {
    return;
  }

  // The width of each column in pixels. Eg (1199 - 10 * 3) / 3 = 389
  const colPixelWidth = (containerWidth - dashboardLayoutConfig.margin * dashboardLayoutConfig.cols[ sizeSetting ]) / dashboardLayoutConfig.cols[ sizeSetting ];

  return Math.round(width / colPixelWidth);
};

const getWidth = (displayType: WidgetDisplayTypes, screenSize: string) => {
  switch (displayType) {
    case WidgetDisplayTypes.BarGraph:
    case WidgetDisplayTypes.LineGraph:
    case WidgetDisplayTypes.Table:
      if (screenSize === "lg") {
        return 2;
      }

      return 1;
    case WidgetDisplayTypes.Number:
    default:
      return 1;
  }
};

const getHeight = (displayType: WidgetDisplayTypes, screenSize: string) => {
  switch (displayType) {
    case WidgetDisplayTypes.BarGraph:
    case WidgetDisplayTypes.LineGraph:
    case WidgetDisplayTypes.Table:
      if (screenSize === "lg") {
        return 10;
      }

      return 8;
    case WidgetDisplayTypes.Number:
    default:
      return 5;
  }
};

/**
 * Get default width for a widget based on its slug or display type
 * Special widgets like RealTimeData and AccountingOverview have fixed sizes
 */
const getDefaultWidth = (slug: WidgetSlug | undefined, displayType: WidgetDisplayTypes, screenSize: string) => {
  switch (slug) {
    case WidgetSlug.RealTimeData:
      return 4;
    case WidgetSlug.AccountingOverview:
      // Accounting widget needs 2 columns on lg to display tables properly
      return screenSize === "lg" ? 2 : 1;
    default:
      return getWidth(displayType, screenSize);
  }
};

/**
 * Get default height for a widget based on its slug or display type
 * Special widgets like RealTimeData and AccountingOverview have fixed sizes
 */
const getDefaultHeight = (slug: WidgetSlug | undefined, displayType: WidgetDisplayTypes, screenSize: string) => {
  switch (slug) {
    case WidgetSlug.RealTimeData:
      return 4;
    case WidgetSlug.AccountingOverview:
      // Accounting widget needs more height to display tabs + table rows + footer
      return screenSize === "lg" ? 14 : 12;
    default:
      return getHeight(displayType, screenSize);
  }
};

interface MergeNewWidgetParams {
  // The current layout being displayed before the widget is added
  layouts: GridLayouts;
  // Dashboard widget to add to the layout
  dashboardWidget: IWidget;
  // Full width of the container that wraps the grid layout
  containerWidth: number;
}

/**
 * This helper takes the current layout and a new widget and merges it into it at the right height and width
 */
export const mergeNewWidget = ({
  layouts, dashboardWidget, containerWidth
}: MergeNewWidgetParams): GridLayouts => {
  // Get the maximum y value from existing items, used for adding new widgets to the bottom
  const maxY = layouts.lg.reduce((max, item) => Math.max(max, item.y + item.h), 0);

  for (const screenSize in layouts) {
    // retrieve widget type layout options to set up fixed layout options object
    const widgetTypeLayoutOptions = dashboardWidget.widgetType?.layoutOptions;

    // Ensure to calc height and width based on row and column sizes as values set in pixels
    const fixedLayoutOptions = {
      minH: calculateHeight(dashboardLayoutConfig.rowHeight, widgetTypeLayoutOptions?.minH),
      maxH: calculateHeight(dashboardLayoutConfig.rowHeight, widgetTypeLayoutOptions?.maxH),
      maxW: calculateWidth({
        width: widgetTypeLayoutOptions?.maxW,
        sizeSetting: screenSize,
        containerWidth
      }),
      minW: calculateWidth({
        width: widgetTypeLayoutOptions?.minW,
        sizeSetting: screenSize,
        containerWidth
      })
    };

    const displayType = dashboardWidget.options.view;

    const widgetLayoutOptions: GridLayout = {
      ...fixedLayoutOptions,
      x: 0,
      y: maxY + 1,
      h: getDefaultHeight(dashboardWidget.widgetType?.slug, displayType, screenSize),
      i: dashboardWidget.id.toString(),
      w: getDefaultWidth(dashboardWidget.widgetType?.slug, displayType, screenSize)
    };

    layouts[ screenSize ].push(widgetLayoutOptions);
  }

  return layouts;
};

export default mergeLayoutOptions;
